import { MouseEvent, ReactNode, RefObject } from 'react';
import { useIntl } from 'react-intl';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { useContentSize } from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  bgLightestBlue,
  borderColorBlue100,
} from '@osmosis/tailwind-ui/styles';
import DashboardSearchInput from './DashboardSearchInput';
import DashboardSearchTrustStrip from './DashboardSearchTrustStrip';
import DashboardSearchPromptChips from './DashboardSearchPromptChips';
import SearchAiToggle, {
  AI_CHAT_TAB_ID,
  SEARCH_AI_PANEL_ID,
  SEARCH_TAB_ID,
  SearchAiMode,
} from './SearchAiToggle';
import WelcomeHeaderTopicsDropdownButton from '../dashboard/WelcomeHeaderTopicsDropdownButton';
import OsmosisAIButton from '../ai/OsmosisAIButton';

type Props = Readonly<{
  showNewSearchDesign: boolean;
  /** Renders the standalone "Ask Osmosis AI" CTA when not in the new design. */
  showAIButton?: boolean;
  mode: SearchAiMode;
  onModeChange: (
    mode: SearchAiMode,
    options?: { focusInput?: boolean },
  ) => void;
  toggleSource?: string;
  centerSearch: boolean;
  isAiChatMode: boolean;
  containerClasses: string;
  dataTestId?: string;
  focusOnMount: boolean;
  aiChatValue: string;
  onAiChatValueChange: (value: string) => void;
  onAiSubmit: (value: string) => void;
  showTopicsDropdownButton: boolean;
  topicsDropdownButtonRef: RefObject<HTMLButtonElement | null>;
  dropdownText: ReactNode;
  isTopicsDropdownVisible: boolean;
  onToggleTopicsDropdown: (
    event: MouseEvent<HTMLDivElement> | MouseEvent<HTMLButtonElement>,
  ) => void;
}>;

/**
 * The dashboard hero's Search/AI Chat toggle, input row and (in the new
 * design) trust strip + prompt chips placeholder. Wraps the content in the
 * bordered card only when showNewSearchDesign is true.
 */
const SearchContainer = ({
  showNewSearchDesign,
  showAIButton = true,
  mode,
  onModeChange,
  toggleSource,
  centerSearch,
  isAiChatMode,
  containerClasses,
  dataTestId,
  focusOnMount,
  aiChatValue,
  onAiChatValueChange,
  onAiSubmit,
  showTopicsDropdownButton,
  topicsDropdownButtonRef,
  dropdownText,
  isTopicsDropdownVisible,
  onToggleTopicsDropdown,
}: Props) => {
  const intl = useIntl();
  const { getClassValue } = useContentSize();

  const responsiveContainerClassname = getClassValue({
    defaultClasses: 'sm:mb-0 sm:flex-row',
    breakpoints: { sm: 'mb-0 flex-row', base: 'mb-3 flex-col' },
  });

  const content = (
    <>
      {showNewSearchDesign && (
        <Flex className="mb-8">
          <SearchAiToggle
            mode={mode}
            onModeChange={onModeChange}
            source={toggleSource}
          />
        </Flex>
      )}

      <Flex
        {...(showNewSearchDesign
          ? {
              role: 'tabpanel',
              id: SEARCH_AI_PANEL_ID,
              'aria-labelledby': isAiChatMode ? AI_CHAT_TAB_ID : SEARCH_TAB_ID,
            }
          : {})}
        className={cn(
          'relative mb-3 w-full flex-col items-center gap-2 self-start rounded',
          centerSearch ? 'justify-center' : 'justify-start',
          responsiveContainerClassname,
        )}
      >
        <DashboardSearchInput
          isAiChatMode={isAiChatMode}
          containerClasses={containerClasses}
          dataTestId={dataTestId}
          focusOnMount={focusOnMount}
          aiChatValue={aiChatValue}
          onAiChatValueChange={onAiChatValueChange}
          onAiSubmit={onAiSubmit}
        />

        {showTopicsDropdownButton ? (
          <WelcomeHeaderTopicsDropdownButton
            ref={topicsDropdownButtonRef}
            dropdownText={dropdownText}
            intl={intl}
            isVisible={isTopicsDropdownVisible}
            onButtonClick={onToggleTopicsDropdown}
          />
        ) : null}

        {!showNewSearchDesign && showAIButton && (
          <OsmosisAIButton
            variant="dashboard"
            classNames={{ button: 'h-[55px]' }}
            source={toggleSource}
            showLoggedOut
          />
        )}
      </Flex>

      {showNewSearchDesign && (
        <>
          <DashboardSearchTrustStrip />
          <DashboardSearchPromptChips disabled={!isAiChatMode} />
        </>
      )}
    </>
  );

  if (!showNewSearchDesign) return content;

  return (
    <Flex
      data-testid="dashboard-search-card"
      className={cn(
        'w-full flex-col rounded border p-8 pb-2 min-h-[243px]',
        bgLightestBlue,
        borderColorBlue100,
      )}
    >
      {mode ? content : null}
    </Flex>
  );
};

export default SearchContainer;
